import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BaseTableDefinitionFactory, EMPTY_TEXT_VALUE } from "./BaseTableDefinitionFactory";
import { IndexType } from "./IndexType";
import { BfxColDef, TableDefinition } from "./TableDefinition";

// adapter imports
import { Injectable, Optional } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SystemConfigPromiseService } from "shared/auth-services";
import { AssetClass, BlotterType } from "shared/cubiq-model-multi-asset";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

import { LoggerService } from "@bfx/logger";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = LoggerService.for(__dirname);

import { ITooltipParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { ProductRenderer } from "shared/cubiq-services/model/orders/ProductRenderer";
import { BfxConfigIDRefHelper } from "../../helper-services/bfx-config-id-ref-helper";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";
import { TimezoneLookupService } from "../lookup/model/impl/TimezoneLookupService";
import { ColumnFilterType } from "../model/ColumnFilterType";
import { SysFeatures } from "../model/core/SysFeatures";
import { ActivationHelper } from "./orders/ActivationHelper";
import { ExpiryHelper } from "./orders/ExpiryHelper";
import { FixingDateSCHelper } from "./orders/FixingDateSCHelper";
import { OrderStateSCHelper } from "./orders/OrderStateSCHelper";
import { ScratchPadOrder } from "./orders/ScratchPadOrder";
import { StatusIconSCHelper } from "./orders/StatusIconSCHelper";
import { TSLLabelHelper } from "./orders/TSLLabelHelper";
import { ValueDateSCHelper } from "./orders/ValueDateSCHelper";
import { OrdersTableMode } from "./OrdersTableMode";
import {
  COLUMN_ACCOUNT_CODE,
  COLUMN_ALLOW_PART_FILLS,
  COLUMN_AUTO_EXECUTE,
  COLUMN_CLIENT_CODE,
  COLUMN_CLIENT_INTENT,
  COLUMN_CL_TRADER,
  COLUMN_CURRENCY,
  COLUMN_EXTERNAL_REF,
  COLUMN_FIXING_DATE,
  COLUMN_FIXING_SOURCE_CODE,
  COLUMN_FWD_NETTING_ID,
  COLUMN_GOOD_FROM,
  COLUMN_GOOD_UNTIL,
  COLUMN_HOLD_STP,
  COLUMN_INSTRUMENT,
  COLUMN_MARKUP,
  COLUMN_MARKUP_TYPE,
  COLUMN_ORDER_AMOUNT,
  COLUMN_ORDER_REQ_RATE,
  COLUMN_ORDER_STATE,
  COLUMN_ORDER_TYPE,
  COLUMN_PRIVATE,
  COLUMN_PRODUCT,
  COLUMN_SAVED_ORDER_ID,
  COLUMN_TRAILING_TRIGGER,
  COLUMN_VAL_DT,
} from "./ScratchPadTableDefinitionFactory.constants";
import { ProductIntentRenderer } from "shared/cubiq-services/model/orders/ProductIntentRenderer";
import { COLUMN_STP_AGG } from "./OrdersTableDefinitionFactory.constants";
import { OrderTypeHelper } from "./orders/OrderTypeHelper";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";

export const COLUMN_ORDER_TRAILING_TRIGGER = "trailingTrigger";

@Injectable()
export class ScratchPadTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected myClientTraderEnabled: boolean;
  protected myTrailingStopEnabled: boolean;
  protected myOrderStateHelper: OrderStateSCHelper;
  protected myValueDateHelper: ValueDateSCHelper;
  protected myActivationHelper: ActivationHelper;
  protected myExpiryHelper: ExpiryHelper;
  protected myStatusIconHelper: StatusIconSCHelper;
  protected myFixingDateHelper: FixingDateSCHelper;
  protected enabledOBO: boolean = false;

  protected tslLabelHelper: TSLLabelHelper;

  constructor(
    protected profileService: ProfilePromiseActionService,
    protected currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    protected translateService: TranslateService,
    protected userLookupService: UserLookupService,
    protected pricingService: DealtPricingService,
    protected traderGroupLookup: TraderGroupLookupService,
    @Optional() protected myClientLookup: ClientLookupService,
    @Optional() protected myAccountLookup: AccountLookupService,
    @Optional() protected ccyLookupService: CcyLookupService,
    @Optional() protected _ccyPairLookupService: CcyPairLookupService,
    @Optional() protected sysConfigService: SystemConfigPromiseService,
    @Optional() protected timeZoneLookup: TimezoneLookupService,
    @Optional() protected configService: BfxConfigService,
    @Optional() protected customFieldLookupService: CustomFieldLookupService,
  ) {
    super(profileService);
    this.myOrderStateHelper = new OrderStateSCHelper(COLUMN_ORDER_STATE, this.translateService);

    this.myValueDateHelper = new ValueDateSCHelper(currencyPairCalendarLookupService);
    this.myActivationHelper = new ActivationHelper();
    this.myExpiryHelper = new ExpiryHelper();
    this.myFixingDateHelper = new FixingDateSCHelper();
    this.myStatusIconHelper = new StatusIconSCHelper(
      userLookupService,
      sysConfigService,
      translateService,
      pricingService,
      traderGroupLookup,
    );

    this.tslLabelHelper = new TSLLabelHelper(pricingService, pricingService);
    this.tslLabelHelper.ordersTableMode = OrdersTableMode.DEALT;

    configService.getUIConfig().then((config) => {
      this.enabledOBO = config && config.general ? config.general.oboEnabled : false;
    });
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return [
      this.sysConfigService.getSystemConfig(SysFeatures.clientTraderUsersEnabled),
      this.sysConfigService.getSystemConfig(SysFeatures.trailingStopsEnabled),
    ];
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.myClientTraderEnabled = this.valueOn(values[0]);
    this.myTrailingStopEnabled = this.valueOn(values[1]);
  }

  private initColdDef(): any {
    return {
      suppressMenu: true,
      cellClass: [DEFAULT_CELL_CLASS],
      valueFormatter: function (params) {
        return params.value ? params.value : "-";
      },
    };
  }

  public createDef(): TableDefinition {
    super.createDef();

    // let position = this.position;
    let td = new TableDefinition(
      COLUMN_EXTERNAL_REF,
      "as_sort_orderId",
      // new RowDefinition([new StatusRowAdapter()])
    );

    let selectionColumn: BfxColDef = this.initColdDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.suppressSorting = true;
    selectionColumn.suppressMovable = true;
    selectionColumn.suppressResize = true;
    selectionColumn.suppressMenu = true;
    selectionColumn.valueGetter = function (params) {
      if (
        params.data &&
        (params.data.isProcessing === true ||
          (params.data.externalClientReference &&
            params.context.isFailedFatFingers &&
            params.context.isFailedFatFingers(params.data.externalClientReference)) ||
          (params.context.isNegativeMarkup &&
            params.context.isNegativeMarkup(params.data.externalClientReference)))
      ) {
        return params.context.getIcon(params.data);
      }
      //NEEDS REVIEW
      return this.myStatusIconHelper.getIcons(params.data);
    }.bind(this);
    selectionColumn.cellRenderer = function (params) {
      return params.value != null ? params.value : "";
    };
    // enforced width on fixed column
    selectionColumn.cellClass = ["status-icon-cell"];
    selectionColumn.width = 71;
    selectionColumn.minWidth = this.threeWs;
    selectionColumn.maxWidth = 71;
    selectionColumn.position = 0;
    selectionColumn.indexType = IndexType.None;
    selectionColumn.showTooltip = false;

    td.addColumn(selectionColumn);

    // order state
    let orderStateColumn: BfxColDef = this.initColdDef();
    orderStateColumn.headerName = "Order State";
    orderStateColumn.indexType = IndexType.String;
    orderStateColumn.field = COLUMN_ORDER_STATE;
    orderStateColumn.minWidth = this.oneWs;
    orderStateColumn.width = this.sixWs;
    orderStateColumn.valueGetter = function (params) {
      if (
        (params.data &&
          params.data.externalClientReference &&
          params.context.isFailedFatFingers &&
          params.context.isFailedFatFingers(params.data.externalClientReference)) ||
        (params.context.isNegativeMarkup &&
          params.context.isNegativeMarkup(params.data.externalClientReference))
      ) {
        return "WARNING";
      }

      if (params.data.isProcessing === true)
        return this.translateService.instant("scratchpad.pending.state");

      return this.myOrderStateHelper.getState(params);
    }.bind(this);
    orderStateColumn.position = 1;
    td.addColumn(orderStateColumn);

    // order id
    let id: BfxColDef = this.initColdDef();
    id.headerName = "Order ID";
    id.indexType = IndexType.String;
    id.field = COLUMN_SAVED_ORDER_ID;
    id.minWidth = this.oneWs;
    id.width = BfxConfigIDRefHelper.getOrderRefWs();
    id.position = 2;
    id.valueFormatter = BfxConfigIDRefHelper.orderRefValueFormatterEmpty;
    id.getQuickFilterText = BfxConfigIDRefHelper.orderRefValueFormatter;
    td.addColumn(id);

    // client id
    let clientId: BfxColDef = this.initColdDef();
    clientId.headerName = "Client ID";
    clientId.indexType = IndexType.String;
    clientId.field = COLUMN_CLIENT_CODE;
    clientId.minWidth = this.oneWs;
    clientId.width = this.sixWs;
    clientId.position = 3;
    td.addColumn(clientId);

    // account id
    let accId: BfxColDef = this.initColdDef();
    accId.headerName = "Account ID";
    accId.indexType = IndexType.String;
    accId.field = COLUMN_ACCOUNT_CODE;
    accId.minWidth = this.oneWs;
    accId.width = this.sixWs;
    accId.position = 4;
    td.addColumn(accId);

    // product spot/fwd/fwd off/ndf etc
    let prodCol: BfxColDef = this.initColdDef();
    prodCol.headerName = "Product";
    prodCol.indexType = IndexType.String;
    prodCol.field = COLUMN_PRODUCT;
    prodCol.minWidth = this.oneWs;
    prodCol.width = this.sixWs;
    prodCol.position = 5;
    prodCol.valueGetter = function (params: { data: ScratchPadOrder }) {
      // Set this to outright by default for display purposes
      const forwardTrackingType =
        params.data.forwardTrackingType ?? CubiqTS.RequestTrackingType.OUTRIGHT_RATE;

      return ProductRenderer.render(
        params.data.product,
        forwardTrackingType,
        params.data.orderType,
      );
    };
    prodCol.cellRenderer = "productRenderer";
    prodCol.tooltipValueGetter = (params) => this.getProductTooltip(params);
    td.addColumn(prodCol);

    //instrument
    let instrumentCol: BfxColDef = this.initColdDef();
    instrumentCol.headerName = "Instrument";
    instrumentCol.indexType = IndexType.String;
    instrumentCol.field = COLUMN_INSTRUMENT;
    instrumentCol.minWidth = this.oneWs;
    instrumentCol.width = this.sixWs;
    instrumentCol.position = 6;
    td.addColumn(instrumentCol);

    // order type
    let orderTypeCd: BfxColDef = this.initColdDef();
    orderTypeCd.headerName = "Order Type";
    orderTypeCd.indexType = IndexType.String;
    orderTypeCd.field = COLUMN_ORDER_TYPE;
    orderTypeCd.minWidth = this.oneWs;
    orderTypeCd.width = 90;
    orderTypeCd.position = 7;
    orderTypeCd.valueGetter = function (params: { data: ScratchPadOrder }) {
      switch (params.data.requestOrderType) {
        case CubiqTS.RequestOrderType.BMARK:
          return "BMARK";
        case CubiqTS.RequestOrderType.CALL:
          return "CALL";
        case CubiqTS.RequestOrderType.SL_ASK:
          return "SL ASK";
        case CubiqTS.RequestOrderType.SL_ATRATE:
          return "SL";
        case CubiqTS.RequestOrderType.SL_BID:
          return "SL BID";
        case CubiqTS.RequestOrderType.TP:
          return "TP";
        case CubiqTS.RequestOrderType.TSL_ASK:
          return "TSL ASK";
        case CubiqTS.RequestOrderType.TSL:
          return "TSL";
        case CubiqTS.RequestOrderType.TSL_BID:
          return "TSL BID";
        case CubiqTS.RequestOrderType.MARKET:
          return "MKT";
      }
      return CubiqTS.RequestOrderType[params.data.requestOrderType];
    };
    td.addColumn(orderTypeCd);

    // base direction
    let baseIntentCol: BfxColDef = this.initColdDef();
    baseIntentCol.headerName = "Dir";
    baseIntentCol.indexType = IndexType.String;
    baseIntentCol.field = COLUMN_CLIENT_INTENT;
    baseIntentCol.minWidth = this.oneWs;
    baseIntentCol.width = this.threeWs;
    baseIntentCol.position = 8;
    baseIntentCol.cellClass = "blotter_center_align_col blotter_priority_col intent_col";
    baseIntentCol.valueGetter = function (params) {
      const order: ScratchPadOrder = params.data;
      if (order?.clientInt !== undefined) {
        if (CubiqTS.RequestOrderType.CALL === order.requestOrderType) {
          return "CALL";
        }
        if (order.product === CubiqTS.Product.SWP) {
          const nearIntent = ProductIntentRenderer.getClientIntentStringFromIntent(order.clientInt);
          const swapIntent =
            order.clientInt === CubiqTS.OrderIntent.BUY
              ? CubiqTS.OrderIntent.SELL
              : CubiqTS.OrderIntent.BUY;
          const farIntent = ProductIntentRenderer.getClientIntentStringFromIntent(swapIntent);
          return `${nearIntent}/${farIntent}`;
        }
        return ProductIntentRenderer.getClientIntentStringFromIntent(order.clientInt);
      }
      return "";
    };
    td.addColumn(baseIntentCol);

    // Base ccy
    let baseCcy: BfxColDef = this.initColdDef();
    baseCcy.headerName = "Ccy";
    baseCcy.indexType = IndexType.String;
    baseCcy.field = COLUMN_CURRENCY;
    baseCcy.minWidth = this.oneWs;
    baseCcy.width = this.threeWs;
    baseCcy.position = 9;
    baseCcy.valueGetter = (params: ValueGetterParams) => {
      if (params.data != null && params.data.requestOrderType == CubiqTS.RequestOrderType.CALL) {
        return null;
      }

      return params.data != null ? params.data[COLUMN_CURRENCY] : null;
    };

    baseCcy.valueFormatter = (params: ValueFormatterParams) => {
      if (
        (params.data != null && params.data.requestOrderType == CubiqTS.RequestOrderType.CALL) ||
        params.value == null
      ) {
        return "-";
      }

      return params.value;
    };

    td.addColumn(baseCcy);

    // order amt
    let baseOrderAmtCd: BfxColDef = this.initColdDef();
    baseOrderAmtCd.headerName = "Order Amt";
    baseOrderAmtCd.indexType = IndexType.NaturalString;
    baseOrderAmtCd.field = COLUMN_ORDER_AMOUNT;
    baseOrderAmtCd.minWidth = 90;
    baseOrderAmtCd.width = this.eightWs;
    baseOrderAmtCd.position = 10;
    baseOrderAmtCd.valueFormatter = this.formatStringAmt;
    td.addColumn(baseOrderAmtCd);

    // dealt order rate
    let requestRate: BfxColDef = this.initColdDef();
    requestRate.headerName = "Request Rate";
    requestRate.indexType = IndexType.String;
    requestRate.field = COLUMN_ORDER_REQ_RATE;
    requestRate.minWidth = this.oneWs;
    requestRate.width = 90;
    requestRate.position = 11;
    td.addColumn(requestRate);

    // value date / tenor
    let vd: BfxColDef = this.initColdDef();
    vd.headerName = "Value Date";
    vd.indexType = IndexType.String;
    vd.field = COLUMN_VAL_DT;
    vd.minWidth = this.oneWs;
    vd.width = 80;
    vd.position = 12;
    vd.valueGetter = (params) => {
      return this.myValueDateHelper.retrieveData(params.data);
    };
    td.addColumn(vd);

    // dealt markup
    let markupCol: BfxColDef = this.initColdDef();
    markupCol.headerName = "Markup";
    markupCol.indexType = IndexType.String;
    markupCol.field = COLUMN_MARKUP;
    markupCol.minWidth = this.oneWs;
    markupCol.width = 60;
    markupCol.position = 13;
    td.addColumn(markupCol);

    // markup type
    let markupType: BfxColDef = this.initColdDef();
    markupType.headerName = "Markup Type";
    markupType.indexType = IndexType.String;
    markupType.field = COLUMN_MARKUP_TYPE;
    markupType.minWidth = this.oneWs;
    markupType.width = this.sixWs;
    markupType.position = 14;
    markupType.valueGetter = function (params) {
      if (params.data.markup) {
        switch (params.data.markupType) {
          case CubiqTS.MarkUpType.Pips:
            return this.translateService.instant(
              `MarkUpType.${CubiqTS.MarkUpType[CubiqTS.MarkUpType.Pips]}`,
            );
          case CubiqTS.MarkUpType.Decimal:
            return this.translateService.instant(
              `MarkUpType.${CubiqTS.MarkUpType[CubiqTS.MarkUpType.Decimal]}`,
            );
          case CubiqTS.MarkUpType.Percentage:
            return this.translateService.instant(
              `MarkUpType.${CubiqTS.MarkUpType[CubiqTS.MarkUpType.Percentage]}`,
            );
          case CubiqTS.MarkUpType.UsdPerMillion:
            return this.translateService.instant(
              `MarkUpType.${CubiqTS.MarkUpType[CubiqTS.MarkUpType.UsdPerMillion]}`,
            );
        }
      }
      return "-";
    }.bind(this);
    td.addColumn(markupType);

    // trailing trigger
    let trTrigger: BfxColDef = this.initColdDef();
    trTrigger.headerName = "Trigger";
    trTrigger.indexType = IndexType.String;
    trTrigger.field = COLUMN_TRAILING_TRIGGER;
    trTrigger.minWidth = this.oneWs;
    trTrigger.width = this.sixWs;
    trTrigger.position = 15;
    trTrigger.valueGetter = function (params) {
      switch (params.data.forwardTrackingType) {
        case CubiqTS.RequestTrackingType.SPOT_RATE:
          return "Spot";
        case CubiqTS.RequestTrackingType.OUTRIGHT_RATE:
          return "Outright";
      }
      return CubiqTS.RequestTrackingType[params.data.forwardTrackingType];
    };
    td.addColumn(trTrigger);

    if (this.myTrailingStopEnabled) {
      // trailing trigger
      let trTrigger: BfxColDef = this.initColdDef();
      trTrigger.headerName = "Trailing Trigger";
      trTrigger.field = COLUMN_ORDER_TRAILING_TRIGGER;

      trTrigger.minWidth = this.oneWs;
      trTrigger.width = this.sevenWs;
      trTrigger.cellClass = [];
      trTrigger.columnFilterType = ColumnFilterType.NONE;
      if (!this.restoreFromConfig(trTrigger)) {
        trTrigger.position = this.position.next();
      }
      trTrigger.valueGetter = function (this: ScratchPadTableDefinitionFactory, params) {
        const order: ScratchPadOrder = params.data;
        let rate = order.tslStartRate;

        if (rate != null && rate != "") {
          try {
            let pair = this._ccyPairLookupService.getCcyPair(order.base, order.nonBase);
            let invertRates = pair.base != order.base;
            let minPrecision: number = invertRates ? pair.spotInvertDecimalsQty : pair.pipsQty;
            let maxPrecision: number = invertRates
              ? pair.fwdInvertDecimalsQty
              : pair.fwdRateDecimalsQty;
            rate = this.pricingService.roundRateToPrecision(rate, minPrecision, maxPrecision);
          } catch (e) {
            logger.out.error(`${this.constructor.name}: Failed to read TSL starting rate ${e}`, e);
          }
        } else {
          rate = "-";
        }

        return rate;
      }.bind(this);

      td.addColumn(trTrigger);

      // trailing pips
      let trPips: BfxColDef = this.initColdDef();
      trPips.headerName = "Trailing Pips";
      trPips.field = "trailingPips";

      trPips.minWidth = this.oneWs;
      trPips.width = this.sixWs;
      trPips.columnFilterType = ColumnFilterType.NONE;
      trPips.cellClass = ["blotter_amt_align"];
      trPips.valueGetter = (params) => {
        const order: ScratchPadOrder = params.data;

        let pips = order.tslDistancePips;

        if (pips != null && pips.length > 0) {
          try {
            let pair = this._ccyPairLookupService.getCcyPair(order.base, order.nonBase);
            let invertRates = pair.base != order.base;
            let pipPrecision = this.pricingService.getPairSpotPipPrecision(pair, invertRates);
            return new CubiqTS.BigNumber(pips).toFixed(pipPrecision);
          } catch (e) {
            logger.out.error(`${this.constructor.name}: Failed to read TSL trailing pips ${e}`, e);
          }
        }

        return "-";
      };

      if (!this.restoreFromConfig(trPips)) {
        trPips.position = this.position.next();
      }

      td.addColumn(trPips);
    }

    // fixing code
    let fixingCd: BfxColDef = this.initColdDef();
    fixingCd.headerName = "Fixing Code";
    fixingCd.indexType = IndexType.String;
    fixingCd.field = COLUMN_FIXING_SOURCE_CODE;
    fixingCd.minWidth = this.oneWs;
    fixingCd.width = this.sixWs;
    fixingCd.position = 16;
    fixingCd.valueGetter = function (params) {
      let val = params.data.fixingSourceCd;
      if (val != null && typeof val === "string") {
        return val;
      }

      if (
        CubiqTS.Product.NDF === params.data["product"] /* order */ ||
        "NDF" === params.data["productType"] /* deal */
      ) {
        let desc: string = this.translateService.instant("fixingSource.custom");
        return desc;
      }

      return "-";
    }.bind(this);
    td.addColumn(fixingCd);

    // fixing date
    let fixingDate: BfxColDef = this.initColdDef();
    fixingDate.headerName = "Fixing Date";
    fixingDate.indexType = IndexType.FixingDate;
    fixingDate.field = COLUMN_FIXING_DATE;
    fixingDate.minWidth = this.oneWs;
    fixingDate.width = this.nineWs;
    fixingDate.position = 17;
    fixingDate.valueGetter = (params) => {
      const order: ScratchPadOrder = params.data;
      if (order?.orderType === CubiqTS.OrderType.BENCHMARK) {
        return order.bmFixingDateLabel;
      }
      return this.myFixingDateHelper.retrieveData(order, COLUMN_FIXING_DATE);
    };
    fixingDate.comparator = this.myFixingDateHelper.compareFixingTimes;
    td.addColumn(fixingDate);

    // good from
    let goodFrom: BfxColDef = this.initColdDef();
    goodFrom.headerName = "Good From";
    goodFrom.indexType = IndexType.Integer;
    goodFrom.field = "activationTimeUnix";
    goodFrom.minWidth = this.oneWs;
    goodFrom.width = this.tenWs;
    goodFrom.position = 18;
    goodFrom.valueFormatter = function (params) {
      return this.myActivationHelper.retrieveData(params.data, COLUMN_GOOD_FROM);
    }.bind(this);
    td.addColumn(goodFrom);

    // good until
    let goodUntil: BfxColDef = this.initColdDef();
    goodUntil.headerName = "Good Until";
    goodUntil.indexType = IndexType.Integer;
    goodUntil.field = "expiratonTimeUnix";
    goodUntil.minWidth = this.oneWs;
    goodUntil.width = this.tenWs;
    goodUntil.position = 19;
    goodUntil.valueFormatter = function (params) {
      return this.myExpiryHelper.retrieveData(params.data, COLUMN_GOOD_UNTIL);
    }.bind(this);
    td.addColumn(goodUntil);

    // trader group
    let dkId: BfxColDef = this.initColdDef();
    dkId.headerName = "Trader Group";
    dkId.indexType = IndexType.String;
    dkId.field = "traderGroup";
    dkId.minWidth = this.oneWs;
    dkId.width = 90;
    dkId.position = 20;
    td.addColumn(dkId);

    // private comments
    let clComm2: BfxColDef = this.initColdDef();
    clComm2.headerName = "Private Comments";
    clComm2.indexType = IndexType.String;
    clComm2.field = "comments2";
    clComm2.minWidth = this.oneWs;
    clComm2.width = this.nineWs;
    clComm2.position = 21;
    td.addColumn(clComm2);

    // client comments
    let clComm1: BfxColDef = this.initColdDef();
    clComm1.headerName = "Client Comments";
    clComm1.indexType = IndexType.String;
    clComm1.field = "comments1";
    clComm1.minWidth = this.oneWs;
    clComm1.width = this.nineWs;
    clComm1.position = 22;
    td.addColumn(clComm1);

    // holdSTP
    let holdSTP: BfxColDef = this.initColdDef();
    holdSTP.headerName = "Hold STP";
    holdSTP.indexType = IndexType.String;
    holdSTP.field = COLUMN_HOLD_STP;
    holdSTP.minWidth = this.oneWs;
    holdSTP.width = this.fiveWs;
    holdSTP.position = 23;
    holdSTP.valueFormatter = this.formatBoolean;
    td.addColumn(holdSTP);

    // auto execute
    let autoExecute: BfxColDef = this.initColdDef();
    autoExecute.headerName = "Auto Execute";
    autoExecute.indexType = IndexType.String;
    autoExecute.field = COLUMN_AUTO_EXECUTE;
    autoExecute.minWidth = this.oneWs;
    autoExecute.width = 90;
    autoExecute.position = 24;
    autoExecute.valueGetter = function (params) {
      if (params.data != null && params.data.fillTypeRestriction !== undefined) {
        if (params.data.fillTypeRestriction === 1) {
          return "No";
        } else {
          return "Yes";
        }
      }
      return "-";
    };
    td.addColumn(autoExecute);

    // private
    let priv: BfxColDef = this.initColdDef();
    priv.headerName = "Private";
    priv.indexType = IndexType.String;
    priv.field = COLUMN_PRIVATE;
    priv.minWidth = this.oneWs;
    priv.width = this.fiveWs;
    priv.position = 25;
    priv.valueFormatter = this.formatBoolean;
    td.addColumn(priv);

    // allow part fills
    let allowPartFills: BfxColDef = this.initColdDef();
    allowPartFills.headerName = "Allow Partial Fills";
    allowPartFills.indexType = IndexType.String;
    allowPartFills.field = COLUMN_ALLOW_PART_FILLS;
    allowPartFills.minWidth = this.oneWs;
    allowPartFills.width = this.eightWs;
    allowPartFills.position = 26;
    allowPartFills.valueGetter = function (params) {
      if (params.data != null && params.data.fillSupport !== undefined) {
        if (params.data.fillSupport === 1) {
          return "No";
        } else {
          return "Yes";
        }
      }
      return "-";
    };
    td.addColumn(allowPartFills);

    // Client Trader
    if (this.myClientTraderEnabled) {
      let clTrader: BfxColDef = this.initColdDef();
      clTrader.headerName = "Client Trader";
      clTrader.indexType = IndexType.String;
      clTrader.field = COLUMN_CL_TRADER;
      clTrader.minWidth = this.oneWs;
      clTrader.width = this.sevenWs;
      clTrader.position = 27;
      td.addColumn(clTrader);
    }

    if (this.enabledOBO) {
      // OBO Client
      let oboClient: BfxColDef = this.initColdDef();
      oboClient.headerName = "OBO Client";
      oboClient.field = "oboClient";
      oboClient.minWidth = this.oneWs;
      oboClient.width = this.sevenWs;
      oboClient.columnFilterType = ColumnFilterType.NONE;
      oboClient.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };
      td.addColumn(oboClient);

      // OBO Account
      let oboAccount: BfxColDef = this.initColdDef();
      oboAccount.headerName = "OBO Account";
      oboAccount.field = "oboAccount";
      oboAccount.minWidth = this.oneWs;
      oboAccount.width = this.sevenWs;
      oboAccount.columnFilterType = ColumnFilterType.NONE;
      oboAccount.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };
      td.addColumn(oboAccount);
    }

    // Custom fields
    let custFieldDefns: Array<CubiqTS.CustomFieldImpl> =
      this.customFieldLookupService.getAllCustomFields();
    custFieldDefns.forEach((item) => {
      if (item.isEnabledFg) {
        let customField: BfxColDef = this.initColdDef();
        customField.headerName = item.displayName;
        customField.field = item.fieldId;
        customField.minWidth = this.oneWs;
        customField.width = this.sevenWs;
        customField.columnFilterType = ColumnFilterType.NONE;
        customField.valueGetter = function (params) {
          let order: ScratchPadOrder = params.data;
          if (order != null) {
            let customValues: Array<CubiqTS.CustomValueImpl> = order.customValues;
            if (customValues != null) {
              for (let i: number = 0; i < customValues.length; i++) {
                if (customValues[i].fieldId === customField.field) {
                  return customValues[i].value;
                }
              }
            }
          }
          return "";
        }.bind(this);

        td.addColumn(customField);
      }
    });

    let stpAgg: BfxColDef = this.initColdDef();
    stpAgg.headerName = "STP Aggregation";
    stpAgg.colSelectorName = "STP Aggreg...";
    stpAgg.field = COLUMN_STP_AGG;
    stpAgg.minWidth = this.oneWs;
    stpAgg.width = this.sixWs;
    stpAgg.valueGetter = (params: ValueGetterParams) => {
      return (
        params.data instanceof ScratchPadOrder &&
        params.data?.rootOrder instanceof CubiqTS.OrderRequestDataImpl &&
        params.data?.rootOrder?.stpAggregationEnabled === true
      );
    };
    stpAgg.columnFilterType = ColumnFilterType.BOOLEAN;

    stpAgg.valueFormatter = (params: ValueFormatterParams) => {
      let val: string = "-";

      if (OrderTypeHelper.canSTPAggregateScratchPadOrder(params.data)) {
        val = params.value === true ? "Yes" : "No";
      }

      return val;
    };

    if (!this.restoreFromConfig(stpAgg)) {
      stpAgg.position = this.position.next();
    }

    td.addColumn(stpAgg);
    // channel
    let channel: BfxColDef = this.initColdDef();
    channel.headerName = "Channel";
    channel.field = "channelCd";
    channel.minWidth = this.oneWs;
    channel.width = this.fiveWs;
    channel.columnFilterType = ColumnFilterType.CHANNEL;

    channel.valueGetter = (params) => {
      let order: ScratchPadOrder = params.data;
      let channelCd: string = order.channelCd;
      return channelCd != null && typeof channelCd === "string" && channelCd.length > 0
        ? channelCd
        : null;
    };
    channel.valueFormatter = (params) => {
      let channelCd: string = params.data.channelCd;
      let displayName: string;
      if (channelCd != null && typeof channelCd === "string" && channelCd.length > 0) {
        displayName = this.myClientLookup.getTradingChannelProfileDisplayName(channelCd);
      }

      return displayName != null && typeof displayName === "string" && displayName.length > 0
        ? displayName
        : EMPTY_TEXT_VALUE;
    };

    td.addColumn(channel);

    // fwdNettingId
    td.addColumn(this.getFwdNettingIdCol());

    // let externalReference: BfxColDef = this.initColdDef();
    // externalReference.headerName = 'External Reference';
    // externalReference.indexType = IndexType.String;
    // externalReference.field = COLUMN_EXTERNAL_REF;
    // externalReference.minWidth = this.sevenWs;
    // externalReference.width = this.sevenWs;
    // externalReference.position = 28;
    // td.addColumn(
    //   externalReference
    // );

    // let errors: BfxColDef = this.initColdDef();
    // errors.headerName = 'Errors';
    // errors.indexType = IndexType.String;
    // errors.field = COLUMN_ORDER_ERRORS;
    // td.addColumn(
    //   errors
    // );

    //////////////////////////////////////////////////////////////////////////////////////////////
    //     let selectionColumn = new ColumnDefinition(
    //         new SelStatusIconsAdapter(this.translateService),
    //         '',
    //         null,
    //         COLUMN_SELECTION,
    //         IndexType.None
    //     );
    //     selectionColumn.sorting = [];
    //     // enforced width on fixed column
    //     selectionColumn.className = 'status-icon-cell';
    //     selectionColumn.width = '71x';
    //     selectionColumn.minWidth = '71px';
    //     selectionColumn.maxWidth = '71px';
    //     selectionColumn.exportable = false;
    //     selectionColumn.position = 0;
    //     selectionColumn.hasSearchKey = false;
    //     selectionColumn.omitFromPicker = true;

    //     td.addColumn(
    //         selectionColumn
    //     );

    //     // order state
    //     let orderStateColumn = new ColumnDefinition(
    //       new OrderStateAdapter(this.translateService),
    //       'Order State',
    //       'Order State',
    //       COLUMN_ORDER_STATE,
    //       IndexType.String
    //   );
    //   orderStateColumn.minWidth = this.fiveWs;
    //   orderStateColumn.width = this.sixWs;
    //   orderStateColumn.position = 1;
    //   td.addColumn(
    //       orderStateColumn
    //   );

    //       // order id
    //     let id =
    //         new ColumnDefinition(
    //             new StringAdapter(),
    //             'Order ID',
    //             'Order ID',
    //             COLUMN_SAVED_ORDER_ID,
    //             IndexType.String
    //         );
    //     id.minWidth = this.fiveWs;
    //     id.width = this.sixWs;
    //     id.position = 2;
    //     id.omitFromPicker = true;
    //     td.addColumn(id);

    //     // client id
    //     let clientId =
    //         new ColumnDefinition(
    //             new ClientIdAdapter(this.myClientLookup),
    //             'Client ID',
    //             'Client ID',
    //             COLUMN_CLIENT_CODE,
    //             IndexType.String
    //         );
    //     clientId.minWidth = this.fiveWs;
    //     clientId.width = this.sixWs;
    //     clientId.position = 3;
    //     clientId.omitFromPicker = true;
    //     td.addColumn(clientId);

    //     // account id
    //     let accId =
    //         new ColumnDefinition(
    //             new AccountIdAdapter(this.myAccountLookup),
    //             'Account ID',
    //             'Account ID',
    //             COLUMN_ACCOUNT_CODE,
    //             IndexType.String
    //         );
    //     accId.minWidth = this.fiveWs;
    //     accId.width = this.sixWs;
    //     accId.position = 4;
    //     clientId.omitFromPicker = true;
    //     td.addColumn(accId);

    //      // product spot/fwd/fwd off/ndf etc
    //     let prodCol =
    //         new ColumnDefinition(
    //             new ProdAdapter(),
    //             'Product',
    //             'Product',
    //             COLUMN_PRODUCT,
    //             IndexType.String
    //         );
    //     prodCol.minWidth = this.fiveWs;
    //     prodCol.width = this.sixWs;
    //     prodCol.position = 5;
    //     prodCol.omitFromPicker = true;
    //     td.addColumn(prodCol);

    //     //instrument
    //     let instrumentCol = new ColumnDefinition(
    //         new StringAdapter(),
    //         'Instrument',
    //         'Instrument',
    //         COLUMN_INSTRUMENT,
    //         IndexType.String
    //     );

    //     instrumentCol.minWidth = this.fiveWs;
    //     instrumentCol.width = this.sixWs;
    //     instrumentCol.omitFromPicker = true;
    //     instrumentCol.position = 6;
    //     td.addColumn(instrumentCol);

    //      // order type
    //     let orderTypeCd = new ColumnDefinition(
    //         new NoHtmlOrderTypeAdapter(),
    //         'Order Type',
    //         'Order Type',
    //         COLUMN_ORDER_TYPE,
    //         IndexType.String
    //     );
    //     orderTypeCd.minWidth = "90px";
    //     orderTypeCd.width = "90px";
    //     orderTypeCd.position = 7;
    //     orderTypeCd.omitFromPicker = true;
    //     orderTypeCd.className = '';
    //     td.addColumn(orderTypeCd);

    //     // base direction
    //      let baseIntentCol = new ColumnDefinition(
    //         new ScratchpadIntentAdapter(),
    //         'Dir',
    //         'Dir',
    //         COLUMN_CLIENT_INTENT,
    //         IndexType.String);
    //         baseIntentCol.minWidth = this.threeWs;
    //         baseIntentCol.width = this.threeWs;
    //         baseIntentCol.className = '';
    //         baseIntentCol.omitFromPicker = true;
    //         baseIntentCol.position = 8;
    //         td.addColumn(baseIntentCol);

    //     // Base ccy
    //       let baseCcy =
    //           new ColumnDefinition(
    //               new StringAdapter(),
    //               'Ccy',
    //               'Ccy',
    //               COLUMN_CURRENCY,
    //               IndexType.String
    //           );
    //       baseCcy.omitFromPicker = true;
    //       baseCcy.minWidth = this.threeWs;
    //       baseCcy.width = this.threeWs;
    //       baseCcy.className = '';
    //       baseCcy.position = 9;
    //       td.addColumn(baseCcy);

    //     let baseOrderAmtCd = new ColumnDefinition(
    //         new ScratchpadAmtAdapter(this.ccyLookupService),
    //         'Order Amt',
    //         'Order Amt',
    //         COLUMN_ORDER_AMOUNT,
    //         IndexType.String
    //     );
    //     baseOrderAmtCd.minWidth = this.sixWs;
    //     baseOrderAmtCd.width = this.eightWs;
    //     baseOrderAmtCd.omitFromPicker = true;
    //     baseOrderAmtCd.className = 'blotter_amt_align';
    //     baseOrderAmtCd.position = 10;
    //     td.addColumn(baseOrderAmtCd);

    //      //   // dealt order rate
    //     let requestRate = new ColumnDefinition(
    //         new RateAdapter(this._ccyPairLookupService, true),
    //         'Request Rate',
    //         'Request Rate',
    //         COLUMN_ORDER_REQ_RATE,
    //         IndexType.NaturalString
    //     );
    //     requestRate.minWidth = "90px";
    //     requestRate.width = "90px";
    //     requestRate.position = 11;
    //     requestRate.omitFromPicker = true;
    //     requestRate.className = 'blotter_amt_align';
    //     td.addColumn(requestRate);

    //     //   // value date / tenor
    //   //   // acts on settleTenor as well as displaying label
    //     let vd = new ColumnDefinition(
    //       new TenorValDtAdapter(),
    //         // new DateAdapter(),
    //         'Value Date',
    //         'Value Date',
    //         COLUMN_VAL_DT,
    //         IndexType.Double,
    //     );

    //     vd.minWidth = "80px";
    //     vd.width = "80px";
    //     vd.position = 12;
    //     vd.omitFromPicker = true;
    //     td.addColumn(vd);

    //         // dealt markup
    //         let markupCol = new ColumnDefinition(
    //             new MrkupAdapter(),
    //             'Markup',
    //             'Markup',
    //             COLUMN_MARKUP,
    //             IndexType.NaturalString
    //         );
    //         markupCol.minWidth = "60px";
    //         markupCol.width = "60px";
    //         markupCol.omitFromPicker = true;
    //         markupCol.className = 'blotter_amt_align';
    //         markupCol.position = 13;
    //         td.addColumn(markupCol);

    //         // dealt markup
    //         let markupType = new ColumnDefinition(
    //           new MarkupTypeAdapter(this.translateService),
    //           'Markup Type',
    //           'Markup Type',
    //           COLUMN_MARKUP_TYPE,
    //           IndexType.String
    //       );
    //       markupType.minWidth = this.fiveWs;
    //       markupType.width = this.sixWs;
    //       markupType.omitFromPicker = true;
    //       markupType.position = 14;
    //       td.addColumn(markupType);

    //         // trailing trigger
    //         let trTrigger =new ColumnDefinition(
    //                 new TrackingTypeAdapter(),
    //                 'Trigger',
    //                 'Trigger',
    //                 COLUMN_TRAILING_TRIGGER,
    //                 IndexType.String,
    //             );
    //         trTrigger.minWidth = this.fiveWs;
    //         trTrigger.width = this.sixWs;
    //         trTrigger.position = 15;
    //         trTrigger.omitFromPicker = true;
    //         td.addColumn(trTrigger);

    //   //       //   // fixing code

    //     let fixingCd = new ColumnDefinition(
    //             new FixingSourceAdapter(this.translateService),
    //             'Fixing Code',
    //             'Fixing Code',
    //             COLUMN_FIXING_SOURCE_CODE,
    //             IndexType.String
    //     );
    //     fixingCd.minWidth = this.fiveWs;
    //     fixingCd.width = this.sixWs;
    //     fixingCd.omitFromPicker = true;
    //     fixingCd.position = 16;
    //     td.addColumn(fixingCd);

    //    // fixing date
    //     let fixingDate = new ColumnDefinition(
    //       new FixingDtAdapter('DD-MMM-YY'),
    //             'Fixing Date',
    //             'Fixing Date',
    //             COLUMN_FIXING_DATE,
    //             IndexType.Double
    //     );
    //     fixingDate.minWidth = "80px";
    //     fixingDate.width = "80px";
    //     fixingDate.omitFromPicker = true;
    //     fixingDate.position = 17;
    //     td.addColumn(fixingDate);

    //     let goodFrom = new ColumnDefinition(
    //       new ActivationAdapter(this.timeZoneLookup),
    //       'Good From',
    //       'Good From',
    //       COLUMN_GOOD_FROM,
    //       IndexType.Double
    //     );
    //     goodFrom.minWidth = this.tenWs;
    //     goodFrom.width = this.tenWs;
    //     goodFrom.omitFromPicker = true;
    //     fixingDate.position = 18;
    //     td.addColumn(goodFrom);

    //     let goodUntil = new ColumnDefinition(
    //       new ExpiryAdapter(this.timeZoneLookup),
    //       'Good Until',
    //       'Good Until',
    //       COLUMN_GOOD_UNTIL,
    //       IndexType.Double
    //     );
    //     goodUntil.minWidth = this.tenWs;
    //     goodUntil.width = this.tenWs;
    //     goodUntil.omitFromPicker = true;
    //     fixingDate.position = 19;
    //     td.addColumn(goodUntil);

    //     //   // trader group
    //     let dkId =
    //         new ColumnDefinition(
    //             new StringAdapter(),
    //             'Trader Group',
    //             'Trader Group',
    //             COLUMN_TRADER_GROUP,
    //             IndexType.String
    //         );
    //     dkId.minWidth = "90px";
    //     dkId.width = "90px";
    //     dkId.position = 20;
    //     dkId.omitFromPicker = true;
    //     td.addColumn(dkId);

    //   //       // private comments
    //         let clComm2 =
    //             new ColumnDefinition(
    //                 new StringAdapter(),
    //                 'Private Comments',
    //                 'Private Comments',
    //                 'comments2',
    //                 IndexType.String
    //             );
    //         clComm2.minWidth = this.nineWs;
    //         clComm2.width = this.nineWs;
    //         clComm2.showTooltip = true;
    //         clComm2.position = 21;
    //         clComm2.omitFromPicker = true;
    //         td.addColumn(clComm2);

    //         //   // client comments
    //     let clComm1 =
    //         new ColumnDefinition(
    //             new StringAdapter(),
    //             'Client Comments',
    //             'Client Comments',
    //             'comments1',
    //             IndexType.String
    //         );
    //     clComm1.minWidth = this.nineWs;
    //     clComm1.width = this.nineWs;
    //     clComm1.showTooltip = true;
    //     clComm1.position = 22;
    //     clComm1.omitFromPicker = true;
    //     td.addColumn(clComm1);

    //     let holdSTP =
    //     new ColumnDefinition(
    //         new BooleanAdapter(),
    //         'Hold STP',
    //         'Hold STP',
    //         COLUMN_HOLD_STP,
    //         IndexType.String
    //     );
    //     holdSTP.minWidth = this.fiveWs;
    //     holdSTP.width = this.fiveWs;
    //     holdSTP.position = 23;
    //     holdSTP.omitFromPicker = true;
    //     holdSTP.className = '';
    //     td.addColumn(holdSTP);

    //     let autoExecute =
    //     new ColumnDefinition(
    //         new ExecutionAdapter(),
    //         'Auto Execute',
    //         'Auto Execute',
    //         COLUMN_AUTO_EXECUTE,
    //         IndexType.String
    //     );
    //     autoExecute.minWidth = "90px";
    //     autoExecute.width = "90px";
    //     autoExecute.position = 24;
    //     autoExecute.omitFromPicker = true;
    //     autoExecute.className = '';
    //     td.addColumn(autoExecute);

    //     let priv =
    //     new ColumnDefinition(
    //         new BooleanAdapter(),
    //         'Private',
    //         'Private',
    //         COLUMN_PRIVATE,
    //         IndexType.String
    //     );
    //     priv.minWidth = this.fiveWs;
    //     priv.width = this.fiveWs;
    //     priv.position = 25;
    //     priv.omitFromPicker = true;
    //     priv.className = '';
    //     td.addColumn(priv);

    //     let allowPartFills =
    //     new ColumnDefinition(
    //         new PartFillAdapter(),
    //         'Allow Partial Fills',
    //         'Allow Partial Fills',
    //         COLUMN_ALLOW_PART_FILLS,
    //         IndexType.String
    //     );
    //     allowPartFills.minWidth = this.eightWs;
    //     allowPartFills.width = this.eightWs;
    //     allowPartFills.position = 26;
    //     allowPartFills.omitFromPicker = true;
    //     allowPartFills.className = '';
    //     td.addColumn(allowPartFills);

    //     if (this.myClientTraderEnabled) {
    //       // Client Trader
    //       let clTrader =
    //           new ColumnDefinition(
    //               new StringAdapter(),
    //               'Client Trader',
    //               'Client Trader',
    //               COLUMN_CL_TRADER,
    //               IndexType.String,
    //           );

    //       clTrader.minWidth = this.sevenWs;
    //       clTrader.width = this.sevenWs;
    //       allowPartFills.position = 27;
    //       allowPartFills.omitFromPicker = true;
    //       td.addColumn(clTrader);
    //   }

    //     let externalReference =
    //         new ColumnDefinition(
    //             new StringAdapter(),
    //             'External Reference',
    //             'External Reference',
    //             COLUMN_EXTERNAL_REF,
    //             IndexType.String
    //         );
    //     externalReference.visible = false;
    //     externalReference.omitFromPicker = true;
    //     td.addColumn(externalReference);

    //     let errors =
    //         new ColumnDefinition(
    //             new ScratchpadErrorAdapter(),
    //             'Errors',
    //             'Errors',
    //             COLUMN_ORDER_ERRORS,
    //             IndexType.String
    //         );
    //     errors.visible = false;
    //     errors.omitFromPicker = true;
    //     td.addColumn(errors);

    // Scratchpad columns are not moveable
    this.supressColumnMove(td);

    return td;
  }
  getFwdNettingIdCol(): BfxColDef {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Fwd Netting ID";
    col.field = COLUMN_FWD_NETTING_ID;
    col.minWidth = this.oneWs;
    col.width = this.fiveWs;
    col.columnFilterType = ColumnFilterType.NONE;

    col.valueGetter = (params) => {
      let order: ScratchPadOrder = params.data;
      let val: string = order.fwdNettingId;
      return val != null && typeof val === "string" && val.length > 0 ? val : null;
    };
    return col;
  }

  getProductTooltip(params: ITooltipParams): string {
    const order: ScratchPadOrder = params.data;
    if (order?.multiAllocation) {
      return order.productTooltip;
    } else {
      return params.value;
    }
  }

  private supressColumnMove(tableDefinition: TableDefinition): void {
    tableDefinition.columns.forEach((column: BfxColDef) => (column.suppressMovable = true));
  }

  public getBlotterType(): BlotterType {
    return BlotterType.SCRATCHPAD;
  }

  public getAssetClass(): AssetClass {
    return AssetClass.FIX;
  }

  protected formatStringAmt(params: ValueFormatterParams) {
    if (
      params.data == null ||
      params.data.requestOrderType == CubiqTS.RequestOrderType.CALL ||
      params.value == null
    ) {
      return "-";
    }

    if (params.value) {
      // Replace "12300000" with "12,300,000"
      let value: string = params.value.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
      if (params.data.product !== CubiqTS.Product.SWP) {
        // Don't want to do any truncation on Swap amounts
        let index: number = value.indexOf(".");
        if (index > 0) {
          value = value.substring(0, index);
        }
      }
      return value;
    }
    return "-";
  }
}
